/**
 * PRDetailDrawer — the slide-over panel an approver sees when they click a PR
 * row in the dashboard. This file is a thin orchestrator: it owns state
 * (open PR, reasoning text, decision note, etc.), runs the API calls, and
 * composes 5 focused child components from `./pr-drawer/`:
 *
 *   DrawerHeader   — sheet title + currency pill + sub-description
 *   SummaryCards   — Amount / Risk / AI-Recommendation + Price/Vendor flag cards
 *   ReasoningPanel — Claude AI risk explainer
 *   ItemsTable     — mobile cards + desktop table + per-item decide + finalize
 *   DecisionPanel  — reason codes + note + Approve/Hold/Reject + override dialog
 *
 * Splitting this way keeps the parent <200 lines and lets each piece be edited
 * in isolation without scrolling through a 700-line file.
 */
import { useEffect, useState } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { prs, auth } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Pencil, Ban, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import DrawerHeader from "./pr-drawer/DrawerHeader";
import SummaryCards from "./pr-drawer/SummaryCards";
import ReasoningPanel from "./pr-drawer/ReasoningPanel";
import ItemsTable from "./pr-drawer/ItemsTable";
import DecisionPanel from "./pr-drawer/DecisionPanel";
import PRTimelineDialog from "./pr-drawer/PRTimelineDialog";

export default function PRDetailDrawer({ vocNo, open, onClose, onDecided, mode = "approve", readOnly = false }) {
  const isAuth = mode === "authorise";
  const [pr, setPR] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reasoning, setReasoning] = useState("");
  const [reasoningBusy, setReasoningBusy] = useState(false);
  const [note, setNote] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [acting, setActing] = useState(false);
  const [pendingPRAction, setPendingPRAction] = useState(null);
  // Cancel-Approved-PR modal state (creator/admin only, ReqStatus=Approved).
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  // Timeline dialog state — read-only, shown for every PR regardless of role.
  const [timelineOpen, setTimelineOpen] = useState(false);

  const itemsWithDecision = (pr?.items || []).filter((x) => x.decision?.action);
  const hasItemDecisions = itemsWithDecision.length > 0;

  // Build the combined "REASON:<code> | <free text>" note string sent with each
  // PR-level decision. Returns null when both halves are empty.
  const buildNote = () => {
    const parts = [];
    if (reasonCode) parts.push(`REASON:${reasonCode}`);
    if (note) parts.push(note);
    return parts.join(" | ") || null;
  };

  // Load PR on open / vocNo change. Resets ALL transient UI state so a
  // previously-typed note / picked reason pill / pending override action
  // from another PR can't leak into the newly-opened one.
  useEffect(() => {
    if (!open || !vocNo) return;
    setPR(null);
    setReasoning("");
    setNote("");
    setReasonCode("");
    setPendingPRAction(null);
    setCancelOpen(false);
    setCancelReason("");
    setLoading(true);
    prs.get(vocNo)
      .then((r) => setPR(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load PR"))
      .finally(() => setLoading(false));
  }, [vocNo, open]);

  const runReasoning = async () => {
    if (!vocNo) return;
    setReasoningBusy(true);
    setReasoning("");
    try {
      const { data } = await prs.reasoning(vocNo);
      setReasoning(data.reasoning || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI reasoning failed");
    } finally {
      setReasoningBusy(false);
    }
  };

  // Map raw action → natural past-tense phrase for toasts, so users see
  // "PR #119609 approved" instead of the old "PR #119609 marked APPROVE".
  const pastTense = (a) => {
    switch (a) {
      case "approve":   return "approved";
      case "authorise": return "authorised";
      case "reject":    return "rejected";
      case "hold":      return "put on hold";
      default:          return a;
    }
  };

  // PR-level decision (approve / hold / reject) — Authorise mode maps 'approve' -> 'authorise'.
  const decide = async (action) => {
    setActing(true);
    const combinedNote = buildNote();
    try {
      if (isAuth) {
        const mapped = action === "approve" ? "authorise" : action;
        await prs.authorise(vocNo, mapped, combinedNote);
        toast.success(`PR #${vocNo} ${pastTense(mapped)}`);
      } else {
        await prs.decide(vocNo, action, combinedNote);
        toast.success(`PR #${vocNo} ${pastTense(action)}`);
      }
      onDecided?.(vocNo, action);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    } finally {
      setActing(false);
    }
  };

  // Per-line-item decision with optimistic UI + rollback on failure.
  const itemDecide = async (it, action) => {
    const prev = it.decision;
    // Optimistically infer the level so the pill shows "L1 REJECTED" for L1
    // actions immediately (matches what the backend will stamp).
    const optimisticLevel = isAuth && !pr?.l1_done ? "L1" : null;
    setPR((cur) => {
      if (!cur) return cur;
      const items = cur.items.map((x) => {
        if (x.icode !== it.icode || x.iname !== it.iname) return x;
        if (action === "clear") {
          return { ...x, decision: { action: null, level: null, by: null, at: null, note: null } };
        }
        let mapped = "HOLD";
        if (action === "approve") mapped = "APPROVED";
        else if (action === "reject") mapped = "REJECTED";
        return {
          ...x,
          decision: { action: mapped, level: optimisticLevel, by: "you", at: "now", note: null },
        };
      });
      return { ...cur, items };
    });
    try {
      const { data } = await prs.itemDecision(vocNo, it.icode, it.iname, action);
      const stamped = data?.level === "L1" ? "L1 " : "";
      toast.success(action === "clear" ? "Item decision cleared" : `Item ${stamped}${pastTense(action)}`);
    } catch (e) {
      // Roll back optimistic change
      setPR((cur) => {
        if (!cur) return cur;
        const items = cur.items.map((x) =>
          x.icode === it.icode && x.iname === it.iname ? { ...x, decision: prev } : x
        );
        return { ...cur, items };
      });
      toast.error(e?.response?.data?.detail || "Item decision failed");
    }
  };

  const finalize = async () => {
    setActing(true);
    try {
      const { data } = await prs.finalize(vocNo);
      if (data.ok) {
        toast.success(`PR finalized as ${data.status}`);
        onDecided?.(vocNo, data.status);
        onClose();
      } else {
        toast.info(data.reason || "PR stays Open");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Finalize failed");
    } finally {
      setActing(false);
    }
  };

  const autoDecide = async () => {
    setActing(true);
    try {
      const { data } = await prs.autoDecide(vocNo);
      const skipped = data.skipped_l1 || 0;
      const msg = skipped > 0
        ? `Auto-decide: ${data.approved} approved · ${data.held} held · ${skipped} skipped (L1-locked)`
        : `Auto-decide: ${data.approved} approved · ${data.held} held`;
      toast.success(msg);
      // Reload PR to show updated item decisions
      const fresh = await prs.get(vocNo);
      setPR(fresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-decide failed");
    } finally {
      setActing(false);
    }
  };

  const unhold = async () => {
    if (!window.confirm(`Un-hold PR #${vocNo}? It will rejoin the active Approve queue.`)) return;
    setActing(true);
    try {
      await prs.unhold(vocNo);
      toast.success(`PR #${vocNo} un-held and back in queue`);
      onDecided?.(vocNo, "unhold");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Un-hold failed");
    } finally {
      setActing(false);
    }
  };

  // Cancel an already-Approved PR. Creator or Admin only. Requires a reason.
  const submitCancel = async () => {
    const reason = cancelReason.trim();
    if (reason.length < 5) {
      toast.error("Please enter a reason (at least 5 characters).");
      return;
    }
    setCancelling(true);
    try {
      await prs.cancel(vocNo, reason);
      toast.success(`PR #${vocNo} cancelled.`);
      onDecided?.(vocNo, "cancel");
      setCancelOpen(false);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[760px] lg:max-w-[920px] overflow-y-auto p-0"
        data-testid="pr-drawer"
      >
        <DrawerHeader pr={pr} loading={loading} />

        {loading && (
          <div className="p-10 text-center text-slate-500">
            <Loader2 className="animate-spin mx-auto mb-2" />
            Loading from MS SQL...
          </div>
        )}

        {pr && (
          <div className="p-6 space-y-6">
            {/* Re-raised lineage banner — shown to L1/L2 so they know this PR was previously rejected */}
            {pr.cloned_from_voc_no && (
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-sm border border-amber-300 bg-amber-50 text-amber-900 text-sm"
                data-testid="cloned-banner"
              >
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-2 py-0.5 rounded-sm">
                  Re-raised
                </span>
                <span>
                  This PR was re-raised from previously rejected{" "}
                  <strong className="mono">PR #{pr.cloned_from_voc_no}</strong>. The creator has revised it and resubmitted.
                </span>
              </div>
            )}

            {pr.bop_manual_note && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-sm border border-red-200 bg-red-50 text-red-900 text-sm"
                data-testid="bop-manual-note-banner"
              >
                <span className="text-[10px] font-bold uppercase tracking-wider bg-red-200 text-red-900 px-2 py-0.5 rounded-sm flex-shrink-0 mt-0.5">
                  AI HOLD
                </span>
                <span>
                  {pr.bop_manual_note}. Requestor review was skipped (BOM item, Smart PR list price, under the limit) — L1 and L2 must approve manually.
                </span>
              </div>
            )}

            {/* Original PR remark — what the requestor typed when creating the PR.
                Helps L1/L2 approvers understand the WHY behind the request. */}
            {pr.pr_remark && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 text-sm"
                data-testid="pr-remark-banner"
              >
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-2 py-0.5 rounded-sm flex-shrink-0 mt-0.5">
                  PR Remark
                </span>
                <span>{pr.pr_remark}</span>
              </div>
            )}

            {/* View History button — always available, opens the PRTimelineDialog
                which lists lifecycle events (raise → self-review → L1 → L2 →
                PO). Kept as a lightweight text-forward button so it doesn't
                compete visually with the Approve / Reject primary actions. */}
            <div className="flex justify-end -mt-1">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="text-xs text-neutral-600 hover:text-neutral-900 h-7 px-2"
                onClick={() => setTimelineOpen(true)}
                data-testid="pr-history-button"
              >
                <History size={13} className="mr-1" />
                View history
              </Button>
            </div>

            {/* Edit banner — shown only when the logged-in user IS the original
                creator AND the requestor self-review hasn't been actioned yet.
                Lets the creator fix mistakes before their PR moves to L1. */}
            {(() => {
              const me = auth.user();
              const isCreator = pr.create_by_emp_no && me?.emp_no &&
                                pr.create_by_emp_no === me.emp_no;
              const stillEditable = (pr.req_status === "Open") &&
                                    !["Approve", "Reject"].includes((pr.reqester_app_ststus || "").trim());
              if (!isCreator || !stillEditable) return null;
              return (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 rounded-sm border border-cyan-200 bg-cyan-50 text-cyan-900 text-sm"
                  data-testid="edit-banner"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-cyan-200 text-cyan-900 px-2 py-0.5 rounded-sm flex-shrink-0">
                    Editable
                  </span>
                  <span className="flex-1">
                    You can still fix this PR before approving — it hasn&apos;t moved to L1 yet.
                  </span>
                  <Button
                    size="sm"
                    onClick={() => { window.location.href = `/pr/edit/${pr.voc_no}`; }}
                    className="bg-cyan-700 hover:bg-cyan-600 text-white flex-shrink-0"
                    data-testid="drawer-edit-btn"
                  >
                    <Pencil size={13} className="mr-1" /> Edit PR
                  </Button>
                </div>
              );
            })()}

            {/* Cancel-Approved-PR banner — creator or Admin can revoke a PR
                that's already been approved. HARD BLOCK once a PO is raised:
                the button is shown DISABLED with an explanatory message so
                users understand why they can't cancel here. */}
            {(() => {
              const me = auth.user();
              const status = (pr.req_status || "").trim();
              const isApproved = status === "Approved" || status === "Approve";
              const isCreator = pr.create_by_emp_no && me?.emp_no &&
                                pr.create_by_emp_no === me.emp_no;
              const isAdmin = (me?.role || "").toLowerCase() === "admin";
              if (!isApproved || (!isCreator && !isAdmin)) return null;
              return (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 rounded-sm border border-rose-200 bg-rose-50 text-rose-900 text-sm"
                  data-testid="cancel-approved-banner"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-200 text-rose-900 px-2 py-0.5 rounded-sm flex-shrink-0">
                    Approved
                  </span>
                  <span className="flex-1">
                    Need to revoke this PR? {isAdmin && !isCreator ? "As Admin, you " : "You "}
                    can cancel it — the ERP status will be set to <strong>Cancel</strong> and the reason is logged for audit.
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setCancelReason(""); setCancelOpen(true); }}
                    className="border-rose-300 text-rose-800 hover:bg-rose-100 flex-shrink-0"
                    data-testid="drawer-cancel-approved-btn"
                  >
                    <Ban size={13} className="mr-1" /> Cancel PR
                  </Button>
                </div>
              );
            })()}

            <SummaryCards pr={pr} />

            <ReasoningPanel
              reasoning={reasoning}
              reasoningBusy={reasoningBusy}
              onGenerate={runReasoning}
            />

            <Separator />

            <ItemsTable
              pr={pr}
              acting={acting}
              onItemDecide={itemDecide}
              onAutoDecide={autoDecide}
              onFinalize={finalize}
              readOnly={readOnly}
              canOverrideL1={(() => {
                // L1 rejection lock: only the PR's L1 Authoriser (Auth_by) OR
                // an Admin may overturn / clear an L1_REJECTED line. Everyone
                // else (including L2 Approvers) sees the row locked.
                const me = auth.user();
                if (!me) return false;
                if ((me.role || "").toLowerCase() === "admin") return true;
                // Drawer opened in L1 authorise mode AND caller is the actual
                // Auth_by on this PR — they own the veto, they can change it.
                if (isAuth && pr.auth_by === me.emp_no) return true;
                return false;
              })()}
            />

            <Separator />

            {!readOnly && (
              <DecisionPanel
                pr={pr}
                mode={mode}
                isAuth={isAuth}
                acting={acting}
                note={note}
                setNote={setNote}
                reasonCode={reasonCode}
                setReasonCode={setReasonCode}
                hasItemDecisions={hasItemDecisions}
                itemsWithDecision={itemsWithDecision}
                pendingPRAction={pendingPRAction}
                setPendingPRAction={setPendingPRAction}
                onDecide={decide}
                onUnhold={unhold}
              />
            )}
            {readOnly && (
              <div
                data-testid="pr-drawer-readonly-banner"
                className="text-[11px] text-slate-500 italic px-3 py-2 bg-slate-50 border border-slate-200 rounded-sm"
              >
                Read-only view from PR Pipeline. To Approve / Reject / Hold this PR, open it from the Approval Dashboard.
              </div>
            )}
          </div>
        )}
      </SheetContent>

      {/* Cancel-Approved-PR confirmation modal — enforces mandatory reason. */}
      <Dialog open={cancelOpen} onOpenChange={(v) => !cancelling && setCancelOpen(v)}>
        <DialogContent data-testid="cancel-pr-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel PR #{vocNo}?</DialogTitle>
            <DialogDescription>
              This will set the ERP status to <strong>Cancel</strong> and cannot be undone
              from this screen. A reason is required and will be saved in the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label htmlFor="cancel-reason" className="text-xs font-medium text-slate-700 uppercase tracking-wider">
              Reason for cancellation <span className="text-rose-600">*</span>
            </label>
            <Textarea
              id="cancel-reason"
              data-testid="cancel-pr-reason-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Duplicate PR, quantity was wrong, vendor changed, no longer required…"
              rows={4}
              disabled={cancelling}
              maxLength={500}
            />
            <div className="text-[11px] text-slate-500 flex justify-between">
              <span>Minimum 5 characters. Visible to Admin in the audit trail.</span>
              <span>{cancelReason.length}/500</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelling}
              data-testid="cancel-pr-dialog-back"
            >
              Keep PR
            </Button>
            <Button
              onClick={submitCancel}
              disabled={cancelling || cancelReason.trim().length < 5}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="cancel-pr-dialog-confirm"
            >
              {cancelling ? (
                <><Loader2 size={14} className="mr-1 animate-spin" /> Cancelling…</>
              ) : (
                <><Ban size={14} className="mr-1" /> Confirm Cancel</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline dialog — mounted outside SheetContent so it renders in its
          own overlay layer above the drawer, not inside the drawer's scroll
          area. Prevents the "modal inside a modal" clipping/scroll trap. */}
      <PRTimelineDialog
        vocNo={vocNo}
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
      />
    </Sheet>
  );
}
